import React, {
  useEffect,
  useContext,
  useMemo,
} from 'react';
import clsx from 'clsx';
import {
  Droppable,
} from '@hello-pangea/dnd';
import {
  MultiMediaResponse,
} from 'dtos';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  Props as SectionProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/types/tabContentProps';
import {
  useGetUnassociatedInsuranceCards,
} from 'views/EMR/components/Insurance/hooks';
import {
  insurancesEventChannel,
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import InsuranceCardMediaThumbnail from 'views/EMR/components/Insurance/InsuranceCard/InsuranceCardMediaThumbnail';
import {
  getUnassociatedInsuranceCardDragId,
  InsuranceCardDragItem,
  unassociatedDroppableId,
} from 'views/EMR/components/Insurance/insuranceCardDnD';

type Props = Partial<SectionProps> & {
  activeDragSourceKey: string | null;
  busyContainerKeys: string[];
  isDraggingInsuranceCard: boolean;
  registerContainerItems: (
    containerKey: string,
    items: InsuranceCardDragItem[],
  ) => void;
  registerUnassociatedRefetch: (refetch: () => Promise<unknown>) => void;
  unregisterContainerItems: (containerKey: string) => void;
  unregisterUnassociatedRefetch: () => void;
};

export default function InsuranceMediaList({
  patient,
  activeDragSourceKey,
  busyContainerKeys,
  isDraggingInsuranceCard,
  registerContainerItems,
  registerUnassociatedRefetch,
  unregisterContainerItems,
  unregisterUnassociatedRefetch,
}: Props) {
  const { isPanel } = useContext(ViewContext);
  const { data: insuranceMedia = [], isLoading, refetch } = useGetUnassociatedInsuranceCards(
    patient?.patientId ?? 0,
  );

  useEffect(() => {
    const unsubscribe = insurancesEventChannel.on(
      'onInsuranceCardAssociationUpdated',
      () => {
        refetch();
      },
    );
    return () => { unsubscribe(); };
  }, [patient?.patientId, refetch]);

  const draggableItems = useMemo(
    () => insuranceMedia
      .filter((media): media is MultiMediaResponse => (media?.mediaId ?? 0) > 0)
      .map((media) => ({
        dragId: getUnassociatedInsuranceCardDragId(media.mediaId ?? 0),
        patientId: patient?.patientId ?? 0,
        mediaId: media.mediaId ?? 0,
        fileName: media.fileName ?? 'Insurance card',
        previewMedia: media,
        sourceType: 'unassociated' as const,
      })),
    [insuranceMedia, patient?.patientId],
  );
  const isDragDisabled = busyContainerKeys.includes(unassociatedDroppableId);
  const isSourceContainer = activeDragSourceKey === unassociatedDroppableId;
  const isValidMoveTarget = isDraggingInsuranceCard
    && !isDragDisabled
    && !isSourceContainer;

  useEffect(() => {
    const unsubscribe = mediaEventChannel.on('onMediaDeleted', () => { refetch(); });
    const unsubscribeFromUpdate = mediaEventChannel.on('onMediaUpdated', () => { refetch(); });
    const unsubscribeFromPatientInsuranceUpdated = insurancesEventChannel.on('onPatientInsuranceUpdated', () => { refetch(); });
    return () => {
      unsubscribe();
      unsubscribeFromUpdate();
      unsubscribeFromPatientInsuranceUpdated();
    };
  }, [refetch]);

  useEffect(() => {
    registerUnassociatedRefetch(refetch);

    return () => {
      unregisterUnassociatedRefetch();
    };
  }, [refetch, registerUnassociatedRefetch, unregisterUnassociatedRefetch]);

  useEffect(() => {
    registerContainerItems(unassociatedDroppableId, draggableItems);

    return () => {
      unregisterContainerItems(unassociatedDroppableId);
    };
  }, [draggableItems, registerContainerItems, unregisterContainerItems]);

  return (
    <div
      className={clsx(
        'flex min-h-0 flex-col justify-start rounded-md bg-white min-w-[18rem] gap-4 ml-4',
        {
          'w-1/4 h-full': !isPanel,
          'w-full min-w-0 ml-0 min-h-[18rem]': isPanel,
        },
      )}
    >
      <h3 className="text-base font-semibold">Unassociated Insurance Cards</h3>
      <Droppable droppableId={unassociatedDroppableId} type="insurance-card">
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={clsx(
              'relative flex min-h-0 flex-col gap-2 rounded-md border p-2',
              'pb-2 transition-all duration-150',
              {
                'overflow-y-auto flex-1': !isPanel,
                'overflow-visible': isPanel,
                'border-blue-300 border-solid bg-blue-50/50 shadow-lg shadow-blue-100/80 ring-1 ring-blue-200/70 -translate-y-0.5':
                  snapshot.isDraggingOver && !isSourceContainer,
                'border-gray-200 bg-gray-50/40':
                  snapshot.isDraggingOver && isSourceContainer,
                'border-blue-200 border-dashed bg-blue-50/20':
                  isValidMoveTarget && !snapshot.isDraggingOver,
                'border-transparent': !snapshot.isDraggingOver && !isValidMoveTarget,
                'opacity-60 pointer-events-none': isDragDisabled,
              },
            )}
          >
            {isLoading && (
              <div className="flex flex-auto justify-start items-center gap-2 text-sm">
                <Spinner size={20} />
                Loading Insurances Cards...
              </div>
            )}
            {!isLoading && !draggableItems.length && !snapshot.isDraggingOver && (
              <p className="absolute left-2 top-2 text-sm">
                Drop here to detach from insurance
              </p>
            )}
            {snapshot.isDraggingOver && !isSourceContainer && (
              <p className="text-sm font-semibold text-blue-700">
                Drop to unassociate card
              </p>
            )}
            {!isLoading && draggableItems.map((item, index) => (
              <InsuranceCardMediaThumbnail
                key={item.dragId}
                item={item}
                index={index}
                isDragDisabled={isDragDisabled}
                isPositionLocked={isDraggingInsuranceCard}
                className={clsx('min-h-[80px] bg-white', { '!min-h-[200px]': isPanel })}
                previewClassName="min-h-[80px]"
                draggingPreviewClassName="!min-h-16"
              />
            ))}
            {isSourceContainer ? provided.placeholder : (
              <div className="hidden">
                {provided.placeholder}
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
}
